import * as React from 'react';
import { Check, ChevronsUpDown, Search, Lock } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu.js';
import { cn } from '../../lib/utils.js';

export interface SelectOption<V = string> {
  value: V;
  label: string;
  /** Render grayed-out and unselectable. */
  disabled?: boolean;
  /** Small pill shown after the label (e.g. "Coming soon"). */
  badge?: string;
  /** Colours the pill. `success` for a state that makes the option
   *  usable — a neutral pill next to a disabled row reads as decoration
   *  rather than as the reason it's disabled. */
  badgeTone?: 'default' | 'success';
  /** Avatar shown before the label, in the trigger and the list. For
   *  options that name a real account — a Page, an Instagram profile —
   *  where the picture identifies it faster than the text does. */
  image?: string | null;
}

export interface SelectProps<V> {
  options: SelectOption<V>[];
  value: V | null | undefined;
  onChange: (next: V) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Cap popover height; the list scrolls past this. */
  maxHeight?: number;
  /** Show the search box at the top of the dropdown. Default: true. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Render the trailing chevron as a padlock — used when the control is
   *  disabled because the feature is gated (not merely busy). */
  locked?: boolean;
}

/** Round avatar for an option. Fixed size and `shrink-0` so a long label
 *  squeezes the text, never the picture — a half-width avatar reads as a
 *  rendering bug. Decorative: the label carries the same meaning, so it
 *  stays out of the accessibility tree. */
function OptionAvatar({ src }: { src: string }) {
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      loading="lazy"
      className="h-5 w-5 shrink-0 rounded-full object-cover ring-1 ring-border"
    />
  );
}

// Light dropdown-style select. Mirrors apps/admin's Select so the manager
// portal has the same look. Items show a checkmark on the active one and
// the popover sticks a search input to the top when `searchable` is on.
export function Select<V extends string>({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled,
  className,
  maxHeight = 320,
  searchable = true,
  searchPlaceholder = 'Search…',
  locked = false,
}: SelectProps<V>) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const searchRef = React.useRef<HTMLInputElement>(null);
  const firstItemRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    if (!searchable) return undefined;
    // Radix focuses the first item on open. Steal focus back to the search
    // box once that pass is over.
    const id = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open, searchable]);

  const selected = options.find((o) => o.value === value);
  const filtered = React.useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <DropdownMenu
      // A disabled select must never open. The trigger button is also
      // `disabled`, but gate the menu state too so Radix can't open it
      // via its own pointer/keyboard handlers.
      open={disabled ? false : open}
      onOpenChange={(next) => {
        if (!disabled) setOpen(next);
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-11 w-full items-center justify-between gap-2 rounded-md border border-border bg-card px-3.5 py-2.5 text-left text-sm transition-colors',
            'hover:border-[hsl(var(--border-strong))] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background focus-visible:border-transparent',
            'data-[state=open]:border-[hsl(var(--border-strong))]',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'dark:bg-card-raised dark:border-white/10',
            className,
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            {selected?.image && <OptionAvatar src={selected.image} />}
            <span
              className={cn(
                'truncate',
                !selected && 'text-muted-foreground/70',
              )}
            >
              {selected ? selected.label : placeholder}
            </span>
          </span>
          {locked ? (
            <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] p-0"
        style={{ maxHeight, overflowY: 'auto' }}
      >
        {searchable && (
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-[hsl(var(--hairline))] bg-popover px-2.5 py-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  firstItemRef.current?.focus();
                  return;
                }
                // Suppress Radix typeahead while the search input owns focus.
                if (e.key !== 'Escape' && e.key !== 'Tab') {
                  e.stopPropagation();
                }
              }}
              placeholder={searchPlaceholder}
              className="flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70"
            />
          </div>
        )}
        <div className="p-1">
          {filtered.length === 0 ? (
            <div className="px-2 py-1.5 text-[13px] text-muted-foreground">
              No matches
            </div>
          ) : (
            filtered.map((opt, i) => {
              const active = opt.value === value;
              return (
                <DropdownMenuItem
                  key={String(opt.value)}
                  ref={i === 0 ? firstItemRef : undefined}
                  disabled={opt.disabled}
                  onSelect={() => {
                    if (!opt.disabled) onChange(opt.value);
                  }}
                  className={cn(
                    active && 'text-foreground',
                    opt.disabled && 'opacity-50',
                  )}
                >
                  {opt.image && <OptionAvatar src={opt.image} />}
                  <span className="flex-1 truncate">{opt.label}</span>
                  {opt.badge && (
                    <span
                      className={cn(
                        'ml-2 shrink-0 rounded-full px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.06em]',
                        opt.badgeTone === 'success'
                          ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {opt.badge}
                    </span>
                  )}
                  {active && <Check />}
                </DropdownMenuItem>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
